import React, { useEffect, useState, useMemo } from 'react';
import { useLeave } from '../context/LeaveContext';
import LeaveForm from '../components/leave/LeaveForm';
import '../styles/MyLeaves.css';
import { Edit } from 'lucide-react';
const toISO = (d) => {
     if (!d) return '';
     const dt = new Date(d);
     if (Number.isNaN(dt.getTime())) return '';
     return dt.toISOString().slice(0, 10);
};

// parse common backend date strings and return a Date or null
const parseDate = (val) => {
     if (!val) return null;
     if (val instanceof Date) return isNaN(val.getTime()) ? null : val;
     const s = String(val).trim();
     // handle "YYYY-MM-DD HH:MM:SS" -> convert to ISO-like "YYYY-MM-DDTHH:MM:SS"
     if (/^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}:\d{2}$/.test(s)) {
          return new Date(s.replace(/\s+/, 'T'));
     }
     // if it's already in YYYY-MM-DD format or ISO
     const dt = new Date(s);
     return Number.isNaN(dt.getTime()) ? null : dt;
};

const pad = (n) => String(n).padStart(2, '0');

// returns DD-MM-YYYY
const formatDateShort = (val) => {
     const dt = parseDate(val);
     if (!dt) return '';
     return `${pad(dt.getDate())}-${pad(dt.getMonth() + 1)}-${dt.getFullYear()}`;
};

// returns DD-MM-YYYY HH:MM:SS (local time)
const formatDateTime = (val) => {
     const dt = parseDate(val);
     if (!dt) return '';
     return `${pad(dt.getDate())}-${pad(dt.getMonth() + 1)}-${dt.getFullYear()} ${pad(dt.getHours())}:${pad(dt.getMinutes())}:${pad(dt.getSeconds())}`;
};

const normalizeForForm = (leave) => ({
     leave_id: leave.leave_id || leave.id || null,
     from_date: leave.from_date || leave.start_date || '',
     to_date: leave.to_date || leave.end_date || '',
     leave_type: leave.leave_type || leave.type || '',
     reason: leave.reason || leave.remarks || '',
     multiple_days:
          (leave.days && Number(parseFloat(leave.days)) > 1) ||
          ((leave.from_date || leave.start_date) && (leave.to_date || leave.end_date) && (leave.from_date || leave.start_date) !== (leave.to_date || leave.end_date)),
     emp_code: leave.emp_code || leave.applied_by || sessionStorage.getItem('user_code') || localStorage.getItem('user_code') || ''
});

const STATUS_LIST = ['All', 'Pending', 'Approved', 'Rejected', 'Pulled Back'];

const MyLeaves = () => {
     const { myLeaves = [], fetchMyLeaves, apply, loading } = useLeave();

     const [openForm, setOpenForm] = useState(false);
     const [editing, setEditing] = useState(null);

     // filters / pagination
     const [from, setFrom] = useState('');
     const [to, setTo] = useState('');
     const [status, setStatus] = useState('All');
     const [q, setQ] = useState('');
     const [page, setPage] = useState(1);
     const pageSizeOptions = [5, 10, 20];
     const [pageSize, setPageSize] = useState(10);

     const userCode = sessionStorage.getItem('user_code') || localStorage.getItem('user_code') || 'me';

     useEffect(() => {
          if (!userCode) return;
          fetchMyLeaves(userCode).catch(() => { });
     }, [fetchMyLeaves, userCode]);

     useEffect(() => {
          setPage(1);
     }, [from, to, status, q, pageSize]);

     const handleEdit = (leave) => {
          setEditing(normalizeForForm(leave));
          setOpenForm(true);
     };

     const handleSubmit = async (payload) => {
          // ensure normalized fields needed by server
          payload.from_date = toISO(payload.from_date);
          payload.to_date = toISO(payload.to_date || payload.from_date);
          payload.total_days = payload.total_days || (payload.multiple_days ? (Math.max(1, (new Date(payload.to_date) - new Date(payload.from_date)) / (1000 * 60 * 60 * 24) + 1)) : 1);
          payload.emp_code = payload.emp_code || userCode || sessionStorage.getItem('user_code') || localStorage.getItem('user_code') || '';
          // include start/end/days for backend compatibility
          payload.start_date = payload.from_date;
          payload.end_date = payload.to_date;
          if (payload.total_days != null) payload.days = String(payload.total_days);

          // call context apply (will use update endpoint if leave_id exists)
          const res = await apply(payload);
          return res;
     };

     // client-side filtering (backend doesn't accept date range in your current route)
     const filtered = useMemo(() => {
          const f = (leave) => {
               const s = (leave.status || leave.leave_status || leave.status || '').toString();
               if (status !== 'All' && status && !s.toLowerCase().includes(status.toLowerCase())) return false;

               const start = leave.start_date || leave.from_date || '';
               const end = leave.end_date || leave.to_date || '';
               if (from) {
                    if (!start || new Date(start) < new Date(from)) return false;
               }
               if (to) {
                    if (!end || new Date(end) > new Date(to)) return false;
               }

               if (q) {
                    const ql = q.toLowerCase();
                    const searchable = `${leave.leave_type || ''} ${leave.reason || ''} ${leave.emp_code || ''} ${leave.id || ''}`.toLowerCase();
                    if (!searchable.includes(ql)) return false;
               }
               return true;
          };
          return (myLeaves || []).filter(f);
     }, [myLeaves, from, to, status, q]);

     const total = filtered.length;
     const totalPages = Math.max(1, Math.ceil(total / pageSize));
     const pageItems = filtered.slice((page - 1) * pageSize, page * pageSize);

     const statusClass = (s) => {
          const st = (s || '').toLowerCase();
          if (st.includes('pending')) return 'status-pending';
          if (st.includes('approved') || st.includes('accept')) return 'status-approved';
          if (st.includes('rejected')) return 'status-rejected';
          if (st.includes('pulled') || st.includes('pull')) return 'status-pulled';
          return 'status-unknown';
     };
     const isEditable = (s) => {
          const st = (s || '').toLowerCase();
          return st.includes('pending');
     };

     return (
          <div className="my-leaves-page">
               <div className="card p-3 mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                         <h5 className="page-title">My Leaves</h5>
                         <div className="filters d-flex gap-2 align-items-center">
                              <input type="date" className="form-control form-control-sm" value={from} onChange={e => setFrom(e.target.value)} />
                              <input type="date" className="form-control form-control-sm" value={to} onChange={e => setTo(e.target.value)} />
                              <select value={status} onChange={e => setStatus(e.target.value)} className="form-select form-select-sm">
                                   {STATUS_LIST.map(s => <option key={s} value={s}>{s}</option>)}
                              </select>
                              <input placeholder="Search" value={q} onChange={e => setQ(e.target.value)} className="form-control form-control-sm" style={{ minWidth: 160 }} />
                              <select value={pageSize} onChange={e => setPageSize(Number(e.target.value))} className="form-select form-select-sm">
                                   {pageSizeOptions.map(n => <option key={n} value={n}>{n} / page</option>)}
                              </select>
                         </div>
                         {/* Add New Leave Button and calendar view button */}
                         <div className="actions">
                              <button className="btn btn-primary btn-sm" onClick={() => { setEditing(null); setOpenForm(true); }}>
                                   Apply New Leave
                              </button>
                              <button className="btn btn-secondary btn-sm ms-2" onClick={() => { /* redirect to  http://localhost:3000/leaves/calendar */
                                   window.location.href = '/leaves/calendar';
                              }}>
                                   View Calendar
                              </button>
                         </div>

                    </div>

                    <div className="table-responsive compact">
                         <table className="table table-sm mb-0">
                              <thead className="table-light">
                                   <tr>
                                        <th style={{ width: 40 }}>#</th>
                                        <th>Type</th>
                                        <th>From</th>
                                        <th>To</th>
                                        <th>Days</th>
                                        <th>Reason</th>
                                        <th>Applied On</th>
                                        <th>Status</th>
                                        <th style={{ width: 90 }}>Actions</th>
                                   </tr>
                              </thead>
                              <tbody>
                                   {loading && (
                                        <tr><td colSpan="9" className="text-center">Loading…</td></tr>
                                   )}
                                   {!loading && pageItems.length === 0 && (
                                        <tr><td colSpan="9" className="text-center text-muted">No leaves found</td></tr>
                                   )}
                                   {pageItems.map((l, i) => (
                                        <tr key={l.id || l.leave_id}
                                             className={`leave-row ${statusClass(l.status || l.leave_status || '')}`}>
                                             <td data-label="#" className="col-index">{(page - 1) * pageSize + i + 1}</td>
                                             <td data-label="Type" className="col-type fw-bold">{l.leave_type || l.type || l.leaveType || '—'}</td>
                                             <td data-label="From" className="col-from">{(l.start_date || l.from_date) ? formatDateShort(l.start_date || l.from_date) : '—'}</td>
                                             <td data-label="To" className="col-to">{(l.end_date || l.to_date) ? formatDateShort(l.end_date || l.to_date) : '—'}</td>
                                             <td data-label="Days" className="col-days">{l.days || l.total_days || l.days_count || '1'}</td>
                                             <td data-label="Reason" className="col-reason text-truncate" style={{ maxWidth: 240 }}>{l.reason || l.remarks || '-'}</td>
                                             <td data-label="Applied On" className="col-applied">{(l.applied_on || l.created_at || l.appliedOn) ? formatDateTime(l.applied_on || l.created_at || l.appliedOn) : '—'}</td>
                                             <td data-label="Status" className="col-status"><span className={`badge ${statusClass(l.status || l.leave_status || '')}`}>{l.status || l.leave_status || l.status || 'Unknown'}</span></td>
                                             <td data-label="Actions" className="col-actions">
                                                  {isEditable(l.status || l.leave_status || '') ? (
                                                       <div className="d-flex">
                                                            <button
                                                                 type="button"
                                                                 className="btn-icon btn-icon-sm btn-icon-primary"
                                                                 onClick={() => handleEdit(l)}
                                                                 title="Edit"
                                                                 aria-label="Edit"
                                                            >
                                                                 <Edit size={14} />
                                                            </button>
                                                       </div>
                                                  ) : (
                                                       <div className="text-muted small">Locked</div>
                                                  )}
                                             </td>
                                        </tr>
                                   ))}
                              </tbody>
                         </table>
                    </div>

                    <div className="d-flex justify-content-between align-items-center mt-2">
                         <div className="text-muted small">Showing {(page - 1) * pageSize + 1} - {Math.min(page * pageSize, total)} of {total}</div>
                         <div className="pagination-compact d-flex align-items-center gap-2">
                              <button className="btn-page" disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))} aria-label="Previous">‹</button>
                              <div className="page-list d-flex gap-1">
                                   {[...Array(totalPages)].map((_, idx) => {
                                        const pnum = idx + 1;
                                        // keep the compact logic
                                        if (totalPages > 7) {
                                             if (pnum !== 1 && pnum !== totalPages && Math.abs(pnum - page) > 2) {
                                                  if (pnum === 2 && page > 4) return <div key={pnum} className="dots">…</div>;
                                                  return null;
                                             }
                                        }
                                        return (
                                             <button
                                                  key={pnum}
                                                  className={`btn-page ${pnum === page ? 'active' : ''}`}
                                                  onClick={() => setPage(pnum)}
                                                  aria-current={pnum === page ? 'page' : undefined}
                                             >
                                                  {pnum}
                                             </button>
                                        );
                                   })}
                              </div>
                              <button className="btn-page" disabled={page >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))} aria-label="Next">›</button>
                         </div>
                    </div>
               </div>

               <LeaveForm
                    open={openForm}
                    initial={editing || {}}
                    emp_code={userCode}
                    onClose={() => { setOpenForm(false); setEditing(null); }}
                    onSubmit={handleSubmit}
               />
          </div>
     );
};

export default MyLeaves;